import { Component, ViewChild } from '@angular/core'
import { AuthService } from '@tv3/services/auth.service'
import { UtilityService } from '@tv3/services/utils/utility.service'
import { ChangePasswordDialogService } from '@tv3/shared/change-password/change-password-dialog/change-password-dialog.service'
import { SkipMasterLayoutService } from '@tv3/services/utils/skip-master-layout.service'
import { environment } from '../../../environments/environment'
import { Destroyable, InfoDialogService, isSomething, LocalStorage, untilDestroy } from '@tokeet/tv3-platform'
import * as R from 'ramda'
import { select, Store } from '@ngrx/store'
import { OpenInventoryOverlay } from '@tv3/store/overlay/overlay.actions'
import { selectActiveProductPlan, selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { isSubscriptionValid, ProductsForPlan } from '@tv3/store/plan/plan.model'
import { map } from 'rxjs/operators'
import { userRoleToFriendlyString } from '@tv3/utils/functions/user-role-to-string'
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { AIResponderSettingsService } from '@tv3/containers/ai-responder/settings/ai-responser-settings.service'
import { KEY_TOKEN, KEY_USER, USER_ROLE, USER_PERMISSIONS } from '@tokeet-frontend/auth'
import { UserMFADialogService } from '@tv3/containers/settings/user-settings/user-mfa-dialog/user-mfa-dialog.service'

declare const window: any

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent extends Destroyable {
  @ViewChild('websiteWidgetMenu', { static: false }) websiteWidgetMenu: BsDropdownDirective

  isNavBarCollapsed = false

  isWebreadyInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Webready }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isPowerReportingInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.PowerReporting }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isRateGenieInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Rategenie }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isAutomataInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Automata }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isSignatureInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Signature }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  isChecklistInactive$ = this.store.pipe(
    select(selectActiveProductPlan, { product: ProductsForPlan.Checklist }),
    map((plan) => !isSubscriptionValid(plan)),
    untilDestroy(this)
  )

  aiResponderSub$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)),
    map((res) => res || {})
  )

  constructor(
    public utility: UtilityService,
    private skipMasterLayout: SkipMasterLayoutService,
    private storage: LocalStorage,
    private infoService: InfoDialogService,
    private store: Store<any>,
    private auth: AuthService,
    private changePasswordDialogService: ChangePasswordDialogService,
    private authService: AuthService,
    private amplitudeService: AmplitudeService,
    private aiSettings: AIResponderSettingsService,
    private mfaDialog: UserMFADialogService
  ) {
    super()
  }

  get name() {
    if (this.authService.user) {
      const firstName = this.authService.user.firstName
      const lastName = this.authService.user.lastName
      return `${firstName} ${lastName}`
    } else {
      return ''
    }
  }

  get role() {
    if (this.authService.user) {
      return userRoleToFriendlyString(this.authService.user.roles)
    } else {
      return ''
    }
  }

  get isVisible(): boolean {
    return !this.skipMasterLayout.check()
  }

  onOpenInventory() {
    this.store.dispatch(OpenInventoryOverlay())
  }

  canSee(
    product: 'power_reporting' | 'ownercenter' | 'rategenie' | 'automata' | 'signature' | 'checklist' | 'webready'
  ) {
    if (this.authService.hasRole('admin')) {
      return true
    }

    if (
      product === 'power_reporting' &&
      (this.authService.hasRole('manager') || this.authService.hasRole('accounting'))
    ) {
      return true
    }

    return !!R.pathOr(0, ['settings', product], this.authService.user)
  }

  switchToTV2() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }

    if (!isSomething(token)) {
      return
    }

    this.storage.remove(KEY_TOKEN)
    this.storage.remove(KEY_USER)
    this.storage.remove(USER_ROLE)
    this.storage.remove(USER_PERMISSIONS)

    window.location.href = `${environment.tv2Url}?token=${token}`
  }

  appStoreUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.appStoreUrl}login?token=${token}`
  }

  rategenieUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.rategenieUrl}login?token=${token}`
  }

  webreadyUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.webreadyUrl}login?token=${token}`
  }

  powerReportingUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.powerReportingUrl}/login?token=${token}`
  }

  automataUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.automataUrl}login?token=${token}`
  }

  signatureUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.signatureUrl}?token=${token}`
  }

  checklistUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.checklistUrl}login#token=${token}`
  }

  reportsUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.reportsUrl}login?token=${token}`
  }

  ownerCenterUrl() {
    let token = this.authService.token
    if (!isSomething(token)) {
      token = this.storage.get(KEY_TOKEN)
    }
    return `${environment.ownerCenterUrl}login?token=${token}`
  }

  logout() {
    this.authService.logout()
  }

  onChangePassword() {
    this.changePasswordDialogService.open(this.authService.user)
  }

  onVisitAppStore() {
    this.amplitudeService.logEvent('visited-app-store')
  }

  onAISettings(isValid: boolean) {
    if (isValid) {
      this.aiSettings.open()
    } else {
      window.open(`${environment.appStoreUrl}/login?redirect=/details/tokeet_ai&token=${this.auth.token}`, '_blank')
    }
  }

  onMFA() {
    this.mfaDialog.open()
  }
}
